import { claimMeasurementContainer } from './container-owner';
import {
  MARKETING_MEASUREMENT_ID,
  getAnalyticsConsent,
  isMarketingOrigin,
} from './measurement';

export const APP_GTM_ID = 'GTM-TPTGFS78';
const eventNames = [
  'agent_created',
  'agent_enabled',
  'agent_disabled',
  'begin_checkout',
] as const;
type AppEventName = (typeof eventNames)[number];
let active = false;
let initialized = false;
let lastPage: string | undefined;
const receipts = new Set<string>();

/** Fixed feature categories only: never expose tenant names or record identifiers. */
export function appPageLocation(href: string): string | null {
  if (!isMarketingOrigin(href, true)) return null;
  const url = new URL(href);
  if (!/^\/home(?:\/|$)/.test(url.pathname)) return null;
  const section = url.pathname.split('/')[3];
  const category = [
    'agents',
    'billing',
    'settings',
    'knowledge-bases',
    'contacts',
    'monitor',
    'integrations',
  ].includes(section ?? '')
    ? section
    : 'workspace';
  return `${url.origin}/app/${category}`;
}

function push(value: Record<string, unknown> | IArguments) {
  try {
    const browser = window as unknown as { dataLayer?: unknown[] };
    (browser.dataLayer ??= []).push(value);
  } catch {
    // Measurement must never interrupt product actions.
  }
}

function command(..._args: unknown[]) {
  // gtag consumes Arguments objects rather than arrays.
  // eslint-disable-next-line prefer-rest-params
  push(arguments);
}

function context() {
  return {
    page_title: 'Leagen app',
    page_location: appPageLocation(window.location.href),
    page_referrer: '',
  };
}

function emit(name: string) {
  push({
    event: 'leagen_app_measurement',
    measurement_event: name,
    leagen_analytics_consent: 'granted',
    measurement: {
      form_name: null,
      form_step: null,
      content_type: null,
      item_id: null,
      ...context(),
    },
  });
}

export function startAppMeasurement(production: boolean): boolean {
  if (
    typeof window === 'undefined' ||
    !production ||
    !appPageLocation(window.location.href) ||
    getAnalyticsConsent() !== 'accepted'
  )
    return false;
  if (!claimMeasurementContainer(APP_GTM_ID)) return false;
  Object.assign(window, { [`ga-disable-${MARKETING_MEASUREMENT_ID}`]: false });
  active = true;
  if (!initialized) {
    command('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    command('set', context());
    push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    initialized = true;
  } else {
    command('consent', 'update', { analytics_storage: 'granted' });
  }
  push({ event: 'leagen_app_init', leagen_analytics_consent: 'granted' });
  return true;
}

export function stopAppMeasurement() {
  if (!active || typeof window === 'undefined') return;
  active = false;
  Object.assign(window, { [`ga-disable-${MARKETING_MEASUREMENT_ID}`]: true });
  push({ leagen_analytics_consent: 'denied' });
  command('consent', 'update', { analytics_storage: 'denied' });
}

export function trackAppPage() {
  if (!active || getAnalyticsConsent() !== 'accepted') return;
  const page = appPageLocation(window.location.href);
  if (!page || page === lastPage) return;
  command('set', context());
  emit('page_view');
  lastPage = page;
}

/** Call after server success. Optional deduplication receipts stay in memory only. */
export function trackAppEvent(name: AppEventName, receipt?: string) {
  if (
    !active ||
    getAnalyticsConsent() !== 'accepted' ||
    !appPageLocation(window.location.href) ||
    !eventNames.includes(name)
  )
    return;
  const key = receipt ? `${name}:${receipt}` : undefined;
  if (key && receipts.has(key)) return;
  if (key) receipts.add(key);
  emit(name);
}
